import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from './ui/button';
import { useAuth } from '../contexts/AuthContext';
import {
  FileSpreadsheet,
  Phone,
  Mail,
  Users,
  Scissors,
  Menu,
  X,
  ChevronDown,
  BookOpen,
  Lightbulb,
  Activity,
  ArrowRight,
  UserCircle,
  AtSign,
  Calculator
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';

const Header = () => {
  const { user, loading } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [resourcesOpen, setResourcesOpen] = useState(false);
  const [toolsOpen, setToolsOpen] = useState(true); // Tools open by default
  const location = useLocation();

  const isHomePage = location.pathname === '/';

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-md border-b border-slate-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2">
            <div className="w-9 h-9 bg-indigo-600 rounded-xl flex items-center justify-center">
              <FileSpreadsheet className="w-5 h-5 text-white" />
            </div>
            <span className="text-lg font-bold text-slate-900 hidden sm:block">CRM Data Cleaner</span>
          </Link>

          {/* Right Side - Nav + Auth (all together on desktop) */}
          <div className="flex items-center gap-6">
            {/* Desktop Nav */}
            <nav className="hidden md:flex items-center gap-6">
              <Link to="/features" className="text-slate-600 hover:text-slate-900 font-medium transition-colors">
                Features
              </Link>
              
              {/* Resources Dropdown */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="flex items-center gap-1 text-slate-600 hover:text-slate-900 font-medium transition-colors">
                    Resources
                    <ChevronDown className="w-4 h-4" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuItem asChild>
                    <Link to="/resources/blog" className="flex items-center gap-3 cursor-pointer">
                      <div className="w-8 h-8 bg-amber-100 rounded-lg flex items-center justify-center">
                        <BookOpen className="w-4 h-4 text-amber-600" />
                      </div>
                      <div>
                        <p className="font-medium">Blog</p>
                        <p className="text-xs text-slate-500">Tips & best practices</p>
                      </div>
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/resources/use-cases" className="flex items-center gap-3 cursor-pointer">
                      <div className="w-8 h-8 bg-teal-100 rounded-lg flex items-center justify-center">
                        <Lightbulb className="w-4 h-4 text-teal-600" />
                      </div>
                      <div>
                        <p className="font-medium">Use Cases</p>
                        <p className="text-xs text-slate-500">See how others use it</p>
                      </div>
                    </Link>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              
              {/* Tools Dropdown */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="flex items-center gap-1 text-slate-600 hover:text-slate-900 font-medium transition-colors">
                    Free Tools
                    <ChevronDown className="w-4 h-4" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuItem asChild>
                  <Link to="/tools/crm-doctor" className="flex items-center gap-3 cursor-pointer">
                    <div className="w-8 h-8 bg-red-100 rounded-lg flex items-center justify-center">
                      <Activity className="w-4 h-4 text-red-600" />
                    </div>
                    <div>
                      <p className="font-medium">CRM Health Audit</p>
                      <p className="text-xs text-slate-500">Free CRM health check</p>
                    </div>
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/tools/phone-formatter" className="flex items-center gap-3 cursor-pointer">
                    <div className="w-8 h-8 bg-emerald-100 rounded-lg flex items-center justify-center">
                      <Phone className="w-4 h-4 text-emerald-600" />
                    </div>
                    <div>
                      <p className="font-medium">Phone Formatter</p>
                      <p className="text-xs text-slate-500">E.164 for HubSpot</p>
                    </div>
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/tools/email-formatter" className="flex items-center gap-3 cursor-pointer">
                    <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                      <Mail className="w-4 h-4 text-blue-600" />
                    </div>
                    <div>
                      <p className="font-medium">Email Fixer</p>
                      <p className="text-xs text-slate-500">Fix typos & validate</p>
                    </div>
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/tools/name-splitter" className="flex items-center gap-3 cursor-pointer">
                    <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                      <Users className="w-4 h-4 text-purple-600" />
                    </div>
                    <div>
                      <p className="font-medium">Name Splitter</p>
                      <p className="text-xs text-slate-500">Split full names</p>
                    </div>
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/tools/split" className="flex items-center gap-3 cursor-pointer">
                    <div className="w-8 h-8 bg-indigo-100 rounded-lg flex items-center justify-center">
                      <Scissors className="w-4 h-4 text-indigo-600" />
                    </div>
                    <div>
                      <p className="font-medium">File Splitter</p>
                      <p className="text-xs text-slate-500">Split large files</p>
                    </div>
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/tools/gender-detector" className="flex items-center gap-3 cursor-pointer">
                    <div className="w-8 h-8 bg-pink-100 rounded-lg flex items-center justify-center">
                      <UserCircle className="w-4 h-4 text-pink-600" />
                    </div>
                    <div>
                      <p className="font-medium">Gender Detector</p>
                      <p className="text-xs text-slate-500">Detect from names</p>
                    </div>
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/tools/email-to-name" className="flex items-center gap-3 cursor-pointer">
                    <div className="w-8 h-8 bg-violet-100 rounded-lg flex items-center justify-center">
                      <AtSign className="w-4 h-4 text-violet-600" />
                    </div>
                    <div>
                      <p className="font-medium">Email to Name</p>
                      <p className="text-xs text-slate-500">Extract names from emails</p>
                    </div>
                  </Link>
                </DropdownMenuItem>
                {/* Divider */}
                <div className="my-2 border-t border-slate-100"></div>
                <DropdownMenuItem asChild>
                  <Link to="/tools/roi-calculator" className="flex items-center gap-3 cursor-pointer">
                    <div className="w-8 h-8 bg-gradient-to-br from-indigo-100 to-purple-100 rounded-lg flex items-center justify-center">
                      <Calculator className="w-4 h-4 text-indigo-600" />
                    </div>
                    <div>
                      <p className="font-medium text-indigo-600">ROI Calculator</p>
                      <p className="text-xs text-slate-500">See your savings</p>
                    </div>
                  </Link>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </nav>

            {/* Auth Buttons */}
            {!loading && user ? (
              <>
                <Link to="/dashboard">
                  <Button className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-full px-5">
                    Dashboard
                  </Button>
                </Link>
              </>
            ) : !loading ? (
              <>
                <Link to="/login" className="text-slate-600 hover:text-slate-900 font-medium hidden sm:block">
                  Log in
                </Link>
                <Link to="/signup">
                  <Button className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-full px-5">
                    Start Free
                  </Button>
                </Link>
              </>
            ) : null}
            
            {/* Mobile menu button */}
            <button 
              className="md:hidden p-2"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>
        
        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-slate-100 animate-in slide-in-from-top-2">
            <div className="max-h-[70vh] overflow-y-auto py-4">
              <div className="flex flex-col gap-1">
                {/* Auth buttons for mobile when not logged in */}
                {!loading && !user && (
                  <div className="flex gap-2 px-4 py-3 border-b border-slate-100 mb-2">
                    <Link 
                      to="/login" 
                      className="flex-1"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      <Button variant="outline" className="w-full">
                        Log in
                      </Button>
                    </Link>
                    <Link 
                      to="/signup" 
                      className="flex-1"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      <Button className="w-full bg-indigo-600 hover:bg-indigo-700 text-white">
                        Sign up
                      </Button>
                    </Link>
                  </div>
                )}
                
                <Link 
                  to="/features" 
                  className="px-4 py-3 text-slate-700 hover:bg-slate-50 rounded-lg"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Features
                </Link>
                
                {/* Resources Section - Collapsible */}
                <div className="border-t border-slate-100 mt-2 pt-2">
                  <button 
                    onClick={() => setResourcesOpen(!resourcesOpen)}
                    className="w-full px-4 py-2 flex items-center justify-between text-xs font-semibold text-slate-400 uppercase tracking-wider hover:bg-slate-50 rounded-lg"
                  >
                    <span>Resources</span>
                    <ChevronDown className={`w-4 h-4 transition-transform ${resourcesOpen ? 'rotate-180' : ''}`} />
                  </button>
                  {resourcesOpen && (
                    <div className="animate-in slide-in-from-top-1">
                      <Link 
                        to="/resources/blog" 
                        className="px-4 py-3 text-slate-700 hover:bg-slate-50 rounded-lg flex items-center gap-2"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        <BookOpen className="w-4 h-4 text-amber-600" /> Blog
                      </Link>
                      <Link 
                        to="/resources/use-cases" 
                        className="px-4 py-3 text-slate-700 hover:bg-slate-50 rounded-lg flex items-center gap-2"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        <Lightbulb className="w-4 h-4 text-teal-600" /> Use Cases
                      </Link>
                    </div>
                  )}
                </div>
                
                {/* Tools Section - Collapsible */}
                <div className="border-t border-slate-100 mt-2 pt-2">
                  <button 
                    onClick={() => setToolsOpen(!toolsOpen)}
                    className="w-full px-4 py-2 flex items-center justify-between text-xs font-semibold text-slate-400 uppercase tracking-wider hover:bg-slate-50 rounded-lg"
                  >
                    <span>Free Tools</span>
                    <ChevronDown className={`w-4 h-4 transition-transform ${toolsOpen ? 'rotate-180' : ''}`} />
                  </button>
                  {toolsOpen && (
                    <div className="animate-in slide-in-from-top-1">
                      <Link 
                        to="/tools/crm-doctor" 
                        className="px-4 py-3 text-slate-700 hover:bg-slate-50 rounded-lg flex items-center gap-2"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        <Activity className="w-4 h-4 text-red-600" /> CRM Health Audit
                      </Link>
                      <Link 
                        to="/tools/phone-formatter" 
                        className="px-4 py-3 text-slate-700 hover:bg-slate-50 rounded-lg flex items-center gap-2"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        <Phone className="w-4 h-4 text-emerald-600" /> Phone Formatter
                      </Link>
                      <Link 
                        to="/tools/email-formatter" 
                        className="px-4 py-3 text-slate-700 hover:bg-slate-50 rounded-lg flex items-center gap-2"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        <Mail className="w-4 h-4 text-blue-600" /> Email Fixer
                      </Link>
                      <Link 
                        to="/tools/name-splitter" 
                        className="px-4 py-3 text-slate-700 hover:bg-slate-50 rounded-lg flex items-center gap-2"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        <Users className="w-4 h-4 text-purple-600" /> Name Splitter
                      </Link>
                      <Link 
                        to="/tools/split" 
                        className="px-4 py-3 text-slate-700 hover:bg-slate-50 rounded-lg flex items-center gap-2"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        <Scissors className="w-4 h-4 text-indigo-600" /> File Splitter
                      </Link>
                      <Link 
                        to="/tools/gender-detector" 
                        className="px-4 py-3 text-slate-700 hover:bg-slate-50 rounded-lg flex items-center gap-2"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        <UserCircle className="w-4 h-4 text-pink-600" /> Gender Detector
                      </Link>
                      <Link 
                        to="/tools/email-to-name" 
                        className="px-4 py-3 text-slate-700 hover:bg-slate-50 rounded-lg flex items-center gap-2"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        <AtSign className="w-4 h-4 text-violet-600" /> Email to Name
                      </Link>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
